import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card, CardContent, Typography, Box,
    IconButton, Chip, Stack
} from '@mui/material';
import { Favorite, FavoriteBorder, LocationOn, Train } from '@mui/icons-material';
import { useNotification } from '../../contexts/NotificationContext';
import { ERROR_MESSAGES } from '../../utils/errorMessages';
import { AMENITY_LABELS } from '../../constants/amenities';
import ImageGallery from '../common/ImageGallery';
import { toggleFavoriteRoom } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { event } from '../../utils/analytics';

const MOVE_IN_TIMING_LABELS = {
    immediate: 'すぐ入居可',
    one_month: '1ヶ月後以降',
    three_months: '3ヶ月後以降',
};

const RoomCard = ({ room, onFavoriteToggle }) => {
    const navigate = useNavigate();
    const { currentUser } = useAuth();
    const { showNotification } = useNotification();
    const [isFavorite, setIsFavorite] = useState(false);
    const [favoriteLoading, setFavoriteLoading] = useState(false);

    const handleCardClick = () => {
        event({ category: 'Room', action: 'View Room Card', label: room._id });
        navigate(`/rooms/${room._id}`);
    };

    const handleFavoriteClick = async (e) => {
        e.stopPropagation();
        if (!currentUser) {
            navigate('/login');
            return;
        }
        if (favoriteLoading) return;
        try {
            setFavoriteLoading(true);
            await toggleFavoriteRoom(room._id);
            setIsFavorite((prev) => !prev);
            if (onFavoriteToggle) onFavoriteToggle();
        } catch (err) {
            showNotification(ERROR_MESSAGES.DEFAULT || 'エラーが発生しました', 'error');
        } finally {
            setFavoriteLoading(false);
        }
    };

    const displayAmenities = (room.roomDetails?.amenities || []).slice(0, 3);

    return (
        <Card
            onClick={handleCardClick}
            sx={{
                cursor: 'pointer',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                '&:hover': { boxShadow: 4 },
                transition: 'box-shadow 0.2s',
            }}
        >
            {/* お気に入りボタン */}
            <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}>
                <IconButton
                    onClick={handleFavoriteClick}
                    size="small"
                    sx={{ bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: 'white' } }}
                    disabled={favoriteLoading}
                >
                    {isFavorite
                        ? <Favorite sx={{ color: 'error.main' }} fontSize="small" />
                        : <FavoriteBorder fontSize="small" />}
                </IconButton>
            </Box>

            {/* 画像 */}
            {room.images && room.images.length > 0 ? (
                <Box sx={{ height: 180, overflow: 'hidden' }} onClick={(e) => e.stopPropagation()}>
                    <ImageGallery images={room.images} height={180} singleImage />
                </Box>
            ) : (
                <Box
                    sx={{
                        height: 180,
                        bgcolor: 'grey.100',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Typography variant="body2" color="text.secondary">画像なし</Typography>
                </Box>
            )}

            <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                {/* 家賃 */}
                <Typography variant="h6" color="primary" fontWeight="bold" gutterBottom>
                    ¥{(room.rentamount || 0).toLocaleString()}
                    <Typography component="span" variant="body2" color="text.secondary"> / 月</Typography>
                </Typography>

                {/* 場所 */}
                <Stack direction="row" alignItems="center" spacing={0.5} mb={0.5}>
                    <LocationOn sx={{ fontSize: 14, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary" noWrap>
                        {room.location?.prefecture} {room.location?.city}
                    </Typography>
                </Stack>

                {/* 最寄り駅 */}
                <Stack direction="row" alignItems="center" spacing={0.5} mb={1}>
                    <Train sx={{ fontSize: 14, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary" noWrap>
                        {room.location?.station}駅 徒歩{room.location?.walkingMinutes}分
                    </Typography>
                </Stack>

                {/* 間取り・広さ */}
                <Stack direction="row" spacing={1} mb={1} flexWrap="wrap" useFlexGap>
                    {room.roomDetails?.layout && (
                        <Chip label={room.roomDetails.layout} size="small" variant="outlined" />
                    )}
                    {room.roomDetails?.size && (
                        <Chip label={`${room.roomDetails.size}㎡`} size="small" variant="outlined" />
                    )}
                    {room.propertyType && (
                        <Chip label={room.propertyType} size="small" variant="outlined" />
                    )}
                </Stack>

                {/* 設備タグ */}
                {displayAmenities.length > 0 && (
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap mb={1}>
                        {displayAmenities.map((amenity) => (
                            <Chip
                                key={amenity}
                                label={AMENITY_LABELS[amenity] || amenity}
                                size="small"
                                sx={{ fontSize: '0.65rem' }}
                            />
                        ))}
                        {(room.roomDetails?.amenities || []).length > 3 && (
                            <Chip
                                label={`+${(room.roomDetails.amenities.length - 3)}`}
                                size="small"
                                sx={{ fontSize: '0.65rem' }}
                            />
                        )}
                    </Stack>
                )}

                {/* 入居時期 */}
                {room.moveInTiming && (
                    <Typography variant="caption" color="text.secondary">
                        {MOVE_IN_TIMING_LABELS[room.moveInTiming] || room.moveInTiming}
                    </Typography>
                )}
            </CardContent>
        </Card>
    );
};

export default RoomCard;
